import React, { useState } from 'react';
import { Plus, Trash2, Home, TrendingUp, Calendar, Check, X, ChevronLeft, ChevronRight, BarChart3, Target, Flame, Sparkles } from 'lucide-react';
import useLocalStorage from './hooks/useLocalStorage';

const MAX_TEXT_LENGTH = 100;
const MAX_UNIT_LENGTH = 20;
const MAX_TARGET = 99999;

const sanitize = (str) => String(str ?? '').replace(/[<>"'`]/g, '').slice(0, MAX_TEXT_LENGTH);

// ── Habits ────────────────────────────────────────────────────

const Habits = ({ setCurrentModule }) => {
  const [view, setView] = useState('today');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [habits, setHabits]           = useLocalStorage('habits', []);
  const [completions, setCompletions] = useLocalStorage('habitCompletions', {});

  if (view === 'today') {
    return <TodayView habits={habits} completions={completions} setCompletions={setCompletions} selectedDate={selectedDate} setView={setView} setCurrentModule={setCurrentModule} />;
  }
  if (view === 'calendar') {
    return <CalendarView habits={habits} completions={completions} selectedDate={selectedDate} setSelectedDate={setSelectedDate} setView={setView} setCurrentModule={setCurrentModule} />;
  }
  if (view === 'analytics') {
    return <AnalyticsView habits={habits} completions={completions} setView={setView} setCurrentModule={setCurrentModule} />;
  }
  if (view === 'manage') {
    return <ManageHabits habits={habits} setHabits={setHabits} setView={setView} setCurrentModule={setCurrentModule} />;
  }
};

// ── TodayView ─────────────────────────────────────────────────

const TodayView = ({ habits, completions, setCompletions, selectedDate, setView, setCurrentModule }) => {
  const dateKey = selectedDate.toISOString().split('T')[0];
  const todayData = completions[dateKey] || {};
  const completedCount = Object.values(todayData).filter(v => v === true || (typeof v === 'number' && v > 0)).length;
  const completionRate = habits.length > 0 ? Math.round((completedCount / habits.length) * 100) : 0;
  const isToday = dateKey === new Date().toISOString().split('T')[0];

  const toggleHabit = (habitId) => {
    setCompletions({ ...completions, [dateKey]: { ...todayData, [habitId]: !todayData[habitId] } });
  };

  const updateQuantity = (habitId, value) => {
    const clamped = Math.min(Math.max(parseFloat(value) || 0, 0), MAX_TARGET);
    setCompletions({ ...completions, [dateKey]: { ...todayData, [habitId]: clamped } });
  };

  const getStreak = (habitId) => {
    let streak = 0;
    for (let i = 0; i < 365; i++) {
      const d = new Date(selectedDate);
      d.setDate(d.getDate() - i);
      const key = d.toISOString().split('T')[0];
      if (completions[key]?.[habitId]) streak++;
      else break;
    }
    return streak;
  };

  return (
    <div className="page">
      <div className="max-w-5xl mx-auto p-4 sm:p-6 md:p-8">
        <button onClick={() => setCurrentModule('dashboard')} className="btn-back" aria-label="Back to Dashboard">
          <Home className="w-4 h-4" />
          <span>Dashboard</span>
        </button>

        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="page-title">
              {isToday ? 'TODAY' : selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' }).toUpperCase()}
            </h1>
            <Target className="w-6 h-6 text-neutral-900" />
          </div>
          <p className="page-subtitle">{completedCount} / {habits.length} Complete · {completionRate}%</p>
        </div>

        <div className="flex flex-wrap gap-3 mb-6">
          <button onClick={() => setView('calendar')} className="flex items-center gap-2 border-2 border-neutral-900 bg-white text-neutral-900 px-4 py-3 text-sm uppercase tracking-wider font-bold hover-lift font-mono">
            <Calendar className="w-4 h-4" /> Calendar
          </button>
          <button onClick={() => setView('analytics')} className="flex items-center gap-2 border-2 border-neutral-900 bg-white text-neutral-900 px-4 py-3 text-sm uppercase tracking-wider font-bold hover-lift font-mono">
            <BarChart3 className="w-4 h-4" /> Stats
          </button>
          <button onClick={() => setView('manage')} className="flex items-center gap-2 bg-neutral-900 text-white px-4 py-3 text-sm uppercase tracking-wider font-bold border-2 border-neutral-900 hover:bg-neutral-700 transition-all shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover-lift-lg font-mono">
            <Plus className="w-4 h-4" /> Manage
          </button>
        </div>

        {habits.length === 0 ? (
          <div className="card-heavy bg-white p-12 sm:p-16 text-center">
            <Target className="w-20 h-20 text-neutral-900 mx-auto mb-4 border-4 border-neutral-900 p-3" />
            <h3 className="text-2xl sm:text-3xl font-serif text-neutral-900 mb-3 uppercase">No Habits Yet</h3>
            <p className="page-subtitle mb-6">Create your first habit to start</p>
            <button onClick={() => setView('manage')} className="bg-neutral-900 text-white px-6 py-3 text-sm uppercase tracking-wider font-bold hover:bg-neutral-700 transition-all card-sm hover-lift-lg font-mono">
              Create Habit
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {habits.map((habit) => {
              const streak = getStreak(habit.id);
              const value = todayData[habit.id];

              return (
                <div key={habit.id} className="border-2 border-neutral-900 bg-white p-4 sm:p-6 hover-lift-lg">
                  <div className="flex items-start gap-4">
                    {habit.type === 'binary' && (
                      <button onClick={() => toggleHabit(habit.id)} className="flex-shrink-0 mt-1">
                        <div className={`w-10 h-10 border-4 transition-all duration-200 hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] ${value ? 'border-neutral-900 bg-neutral-900' : 'border-neutral-900 hover:bg-neutral-100'}`}>
                          {value && <Check className="w-full h-full text-white p-1" strokeWidth={4} />}
                        </div>
                      </button>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg sm:text-xl font-bold text-neutral-900 font-mono uppercase truncate">{habit.name}</h3>
                          {habit.description && <p className="text-sm text-neutral-600 mt-1 font-mono">{habit.description}</p>}
                        </div>
                        {streak > 0 && (
                          <div className="ml-4 flex-shrink-0 border-2 border-neutral-900 bg-orange-100 px-3 py-2">
                            <div className="flex items-center gap-2">
                              <Flame className="w-5 h-5 text-orange-600" />
                              <div className="text-xl font-bold text-neutral-900 font-mono">{streak}</div>
                            </div>
                            <div className="text-xs uppercase tracking-wider text-neutral-700 font-mono font-bold">Streak</div>
                          </div>
                        )}
                      </div>

                      {habit.type === 'quantity' && (
                        <div className="border-2 border-neutral-900 p-3 bg-neutral-50">
                          <div className="flex items-center gap-4">
                            <input
                              type="number" step="0.1" min="0" max={MAX_TARGET}
                              value={value || ''}
                              onChange={e => updateQuantity(habit.id, e.target.value)}
                              placeholder="0"
                              className="w-32 border-2 border-neutral-900 bg-white px-3 py-2 text-lg text-neutral-900 focus:outline-none focus:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all font-mono font-bold"
                            />
                            <span className="text-neutral-900 font-mono font-bold uppercase">{habit.unit}</span>
                            {habit.target && <span className="text-neutral-600 text-sm font-mono">Target: {habit.target} {habit.unit}</span>}
                          </div>
                        </div>
                      )}

                      {habit.type === 'time' && (
                        <div className="border-2 border-neutral-900 p-3 bg-neutral-50">
                          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-2">
                            <div className="flex items-center gap-2">
                              <input
                                type="number" min="0" max="23"
                                value={Math.floor((value || 0) / 60)}
                                onChange={e => updateQuantity(habit.id, (parseInt(e.target.value) || 0) * 60 + ((value || 0) % 60))}
                                placeholder="0"
                                className="w-20 border-2 border-neutral-900 bg-white px-3 py-2 text-lg text-neutral-900 focus:outline-none focus:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all font-mono font-bold"
                              />
                              <span className="text-neutral-900 font-mono font-bold uppercase">Hours</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                type="number" min="0" max="59"
                                value={(value || 0) % 60}
                                onChange={e => updateQuantity(habit.id, Math.floor((value || 0) / 60) * 60 + (parseInt(e.target.value) || 0))}
                                placeholder="0"
                                className="w-20 border-2 border-neutral-900 bg-white px-3 py-2 text-lg text-neutral-900 focus:outline-none focus:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all font-mono font-bold"
                              />
                              <span className="text-neutral-900 font-mono font-bold uppercase">Min</span>
                            </div>
                          </div>
                          {(habit.targetHours || habit.targetMinutes) && (
                            <span className="text-neutral-600 text-sm font-mono">Target: {habit.targetHours || 0}h {habit.targetMinutes || 0}m</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ── CalendarView ──────────────────────────────────────────────

const CalendarView = ({ habits, completions, selectedDate, setSelectedDate, setView, setCurrentModule }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedHabit, setSelectedHabit] = useState(null);

  const days = (() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const startDow = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return [
      ...Array(startDow).fill(null),
      ...Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1)),
    ];
  })();

  const getCompletionRate = (date) => {
    if (!date) return 0;
    const key = date.toISOString().split('T')[0];
    const dayData = completions[key] || {};
    const completed = Object.values(dayData).filter(v => v === true || (typeof v === 'number' && v > 0)).length;
    return habits.length > 0 ? completed / habits.length : 0;
  };

  const habitToShow = selectedHabit ? habits.find(h => h.id === selectedHabit) : null;

  return (
    <div className="page">
      <div className="page-content-lg">
        <button onClick={() => setCurrentModule('dashboard')} className="btn-back">
          <Home className="w-4 h-4" /><span>Dashboard</span>
        </button>

        <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-end justify-between border-b-4 border-neutral-900 pb-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="page-title">Calendar</h1>
              <Calendar className="w-6 h-6 text-neutral-900" />
            </div>
            <p className="page-subtitle">Track consistency</p>
          </div>
          <button onClick={() => setView('today')} className="mt-4 sm:mt-0 bg-neutral-900 text-white px-4 py-3 text-sm uppercase tracking-wider font-bold hover:bg-neutral-700 transition-all card-sm hover-lift-lg font-mono">
            ← Back
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1">
            <div className="card-md bg-white p-4 lg:sticky lg:top-8">
              <h3 className="text-sm uppercase tracking-wider text-neutral-900 mb-4 font-bold font-mono border-b-2 border-neutral-900 pb-2">Filter</h3>
              <div className="space-y-2">
                {[{ id: null, name: 'All Habits' }, ...habits].map(h => (
                  <button
                    key={h.id ?? 'all'}
                    onClick={() => setSelectedHabit(h.id)}
                    className={`w-full text-left px-3 py-2 text-sm transition-all border-2 border-neutral-900 font-mono font-bold uppercase truncate ${
                      selectedHabit === h.id ? 'bg-neutral-900 text-white shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]' : 'bg-white hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:-translate-x-0.5 hover:-translate-y-0.5'
                    }`}
                  >
                    {h.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="card-md bg-white p-4 sm:p-6">
              <div className="flex items-center justify-between mb-6 border-b-2 border-neutral-900 pb-4">
                <h2 className="section-title">{currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h2>
                <div className="flex gap-2">
                  {[[-1, <ChevronLeft className="w-5 h-5"/>], [null, 'Today'], [1, <ChevronRight className="w-5 h-5"/>]].map(([dir, content], i) => (
                    <button
                      key={i}
                      onClick={() => {
                        if (dir === null) { setCurrentMonth(new Date()); return; }
                        const d = new Date(currentMonth); d.setMonth(d.getMonth() + dir); setCurrentMonth(d);
                      }}
                      className="border-2 border-neutral-900 text-neutral-900 p-2 hover:bg-neutral-900 hover:text-white transition-all hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] font-mono font-bold text-sm px-3"
                    >
                      {content}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-7 gap-2 mb-3">
                {['SUN','MON','TUE','WED','THU','FRI','SAT'].map(d => (
                  <div key={d} className="text-center text-xs uppercase tracking-wider text-neutral-900 py-2 font-mono font-bold border-b-2 border-neutral-900">{d}</div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-2">
                {days.map((day, idx) => {
                  if (!day) return <div key={idx} className="aspect-square" />;
                  const isToday = day.toDateString() === new Date().toDateString();
                  let intensity = 0;
                  if (selectedHabit) {
                    const val = completions[day.toISOString().split('T')[0]]?.[selectedHabit];
                    if (val === true || (typeof val === 'number' && val > 0)) intensity = 1;
                  } else {
                    intensity = getCompletionRate(day);
                  }
                  return (
                    <button
                      key={idx}
                      onClick={() => { setSelectedDate(day); setView('today'); }}
                      className={`aspect-square border-2 transition-all flex items-center justify-center relative font-mono font-bold hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:-translate-x-0.5 hover:-translate-y-0.5 ${isToday ? 'border-4 border-neutral-900' : 'border-neutral-900'} ${intensity === 0 ? 'bg-white' : intensity < 0.33 ? 'bg-green-200' : intensity < 0.66 ? 'bg-green-400' : 'bg-green-600'}`}
                    >
                      <span className={`text-sm ${intensity > 0.5 ? 'text-white' : 'text-neutral-900'}`}>{day.getDate()}</span>
                      {intensity === 1 && <Check className="w-4 h-4 text-white absolute top-0.5 right-0.5" strokeWidth={3} />}
                    </button>
                  );
                })}
              </div>

              <div className="mt-6 pt-4 border-t-2 border-neutral-900">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-neutral-900 font-mono font-bold uppercase text-xs">Less</span>
                    {['bg-white','bg-green-200','bg-green-400','bg-green-600'].map(c => <div key={c} className={`w-6 h-6 border-2 border-neutral-900 ${c}`} />)}
                    <span className="text-neutral-900 font-mono font-bold uppercase text-xs">More</span>
                  </div>
                  {habitToShow && (
                    <div className="border-2 border-neutral-900 bg-yellow-100 px-3 py-2">
                      <span className="text-neutral-900 font-mono font-bold uppercase text-xs">Filter: {habitToShow.name}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── AnalyticsView ─────────────────────────────────────────────

const AnalyticsView = ({ habits, completions, setView, setCurrentModule }) => {
  const getLast30Days = () =>
    Array.from({ length: 30 }, (_, i) => {
      const d = new Date(); d.setDate(d.getDate() - (29 - i));
      return d.toISOString().split('T')[0];
    });

  const getHabitStats = (habitId) => {
    const last30 = getLast30Days();
    let completed = 0, totalValue = 0, currentStreak = 0, longestStreak = 0;
    const habit = habits.find(h => h.id === habitId);

    Object.keys(completions).forEach(date => {
      const value = completions[date][habitId];
      if (value === true || (typeof value === 'number' && value > 0)) {
        if (last30.includes(date)) completed++;
        if (typeof value === 'number') totalValue += value;
      }
    });

    let tempStreak = 0;
    for (let i = 0; i < 365; i++) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const key = d.toISOString().split('T')[0];
      if (completions[key]?.[habitId]) {
        tempStreak++;
        if (tempStreak > longestStreak) longestStreak = tempStreak;
        if (i === 0 || currentStreak === tempStreak - 1) currentStreak = tempStreak;
      } else if (i > 0) {
        tempStreak = 0;
      }
    }

    return {
      habit,
      completed,
      completionRate: (completed / 30) * 100,
      currentStreak,
      longestStreak,
      averageValue: habit.type !== 'binary' ? (completed > 0 ? totalValue / completed : 0) : null,
    };
  };

  const stats = habits.map(h => getHabitStats(h.id)).sort((a, b) => b.completionRate - a.completionRate);
  const overallRate = stats.length > 0 ? Math.round(stats.reduce((s, x) => s + x.completionRate, 0) / stats.length) : 0;

  return (
    <div className="page">
      <div className="page-content">
        <button onClick={() => setCurrentModule('dashboard')} className="btn-back">
          <Home className="w-4 h-4" /><span>Dashboard</span>
        </button>

        <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-end justify-between border-b-4 border-neutral-900 pb-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="page-title">Analytics</h1>
              <BarChart3 className="w-6 h-6 text-neutral-900" />
            </div>
            <p className="page-subtitle">Last 30 Days</p>
          </div>
          <button onClick={() => setView('today')} className="mt-4 sm:mt-0 bg-neutral-900 text-white px-4 py-3 text-sm uppercase tracking-wider font-bold hover:bg-neutral-700 transition-all card-sm hover-lift-lg font-mono">
            ← Back
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-6">
          {[
            { value: `${overallRate}%`, label: 'Overall' },
            { value: habits.length,    label: 'Active' },
            { value: Math.max(...stats.map(s => s.longestStreak), 0), label: 'Best Streak', flame: true },
          ].map(({ value, label, flame }) => (
            <div key={label} className="card-md bg-white p-4 sm:p-6 text-center hover-lift-xl">
              <div className="flex items-center justify-center gap-2 mb-3">
                {flame && <Flame className="w-8 h-8 sm:w-10 sm:h-10 text-orange-600" />}
                <div className="text-5xl sm:text-6xl font-bold text-neutral-900 font-mono">{value}</div>
              </div>
              <div className="text-xs sm:text-sm uppercase tracking-wider text-neutral-700 font-bold font-mono border-t-2 border-neutral-900 pt-3">{label}</div>
            </div>
          ))}
        </div>

        <div className="card-md bg-white">
          <div className="card-header-dark sm:p-6">
            <h2 className="section-title text-white">Performance</h2>
          </div>
          <div className="p-4 sm:p-6 space-y-4">
            {stats.length === 0 ? (
              <div className="text-center py-12">
                <BarChart3 className="w-16 h-16 text-neutral-400 mx-auto mb-4 border-4 border-neutral-900 p-2" />
                <p className="text-neutral-500 font-mono uppercase">No data yet</p>
              </div>
            ) : stats.map(stat => (
              <div key={stat.habit.id} className="border-2 border-neutral-900 p-4 bg-white hover-lift">
                <div className="flex items-start justify-between mb-3 pb-3 border-b-2 border-neutral-900">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-neutral-900 font-mono uppercase">{stat.habit.name}</h3>
                    <p className="text-sm text-neutral-700 font-mono mt-1">{stat.completed}/30 days · {Math.round(stat.completionRate)}%</p>
                  </div>
                  <div className="flex items-center gap-2 border-2 border-neutral-900 bg-orange-100 px-3 py-2">
                    <Flame className="w-5 h-5 text-orange-600" />
                    <div className="text-2xl font-bold text-neutral-900 font-mono">{stat.currentStreak}</div>
                  </div>
                </div>

                <div className="progress-track mb-4">
                  <div className="progress-fill-green" style={{ width: `${stat.completionRate}%` }} />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-center text-sm">
                  {[
                    { value: stat.longestStreak, label: 'Best' },
                    ...(stat.averageValue !== null ? [{ value: stat.averageValue.toFixed(1), label: `Avg ${stat.habit.unit || 'min'}` }] : []),
                    { value: 30 - stat.completed, label: 'Missed' },
                  ].map(({ value, label }) => (
                    <div key={label} className="border-2 border-neutral-900 p-2 bg-neutral-50">
                      <div className="text-neutral-900 font-bold font-mono text-lg">{value}</div>
                      <div className="text-neutral-700 text-xs font-mono font-bold uppercase">{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── ManageHabits ──────────────────────────────────────────────

const ManageHabits = ({ habits, setHabits, setView, setCurrentModule }) => {
  const [newHabit, setNewHabit] = useState({ name: '', description: '', type: 'binary', unit: '', target: '' });
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const addHabit = () => {
    const name = sanitize(newHabit.name);
    if (!name.trim()) { setError('Habit name is required'); return; }
    setError(null);
    setHabits([...habits, {
      id: Date.now(),
      ...newHabit,
      name,
      description: sanitize(newHabit.description),
      unit: sanitize(newHabit.unit).slice(0, MAX_UNIT_LENGTH),
      target: newHabit.target ? Math.min(Math.abs(parseFloat(newHabit.target) || 0), MAX_TARGET) : '',
      createdAt: new Date().toISOString(),
    }]);
    setNewHabit({ name: '', description: '', type: 'binary', unit: '', target: '' });
  };

  const deleteHabit = (id) => {
    setHabits(habits.filter(h => h.id !== id));
    setDeleteConfirm(null);
  };

  const typeButtons = [
    { type: 'binary',   label: '✓ Checkbox', sub: 'Done / Not Done' },
    { type: 'quantity', label: '# Quantity',  sub: 'Track numbers' },
    { type: 'time',     label: '⏱ Time',      sub: 'Hours & minutes' },
  ];

  return (
    <div className="page">
      <div className="max-w-5xl mx-auto p-4 sm:p-6 md:p-8">
        <button onClick={() => setCurrentModule('dashboard')} className="btn-back">
          <Home className="w-4 h-4" /><span>Dashboard</span>
        </button>

        <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-end justify-between border-b-4 border-neutral-900 pb-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="page-title">Manage</h1>
              <Sparkles className="w-6 h-6 text-neutral-900" />
            </div>
            <p className="page-subtitle">Create & organize</p>
          </div>
          <button onClick={() => setView('today')} className="mt-4 sm:mt-0 bg-neutral-900 text-white px-4 py-3 text-sm uppercase tracking-wider font-bold hover:bg-neutral-700 transition-all card-sm hover-lift-lg font-mono">
            ← Back
          </button>
        </div>

        {/* Add form */}
        <div className="card-md bg-white p-4 sm:p-6 mb-6">
          <div className="border-b-2 border-neutral-900 pb-4 mb-6">
            <h2 className="section-title">Add New Habit</h2>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border-2 border-red-600 p-3 text-red-700 text-sm font-mono">{error}</div>
          )}

          <div className="space-y-4">
            <div>
              <label className="label mb-2">Habit Name *</label>
              <input
                type="text" value={newHabit.name} maxLength={MAX_TEXT_LENGTH}
                onChange={e => { setError(null); setNewHabit({ ...newHabit, name: e.target.value }); }}
                placeholder="e.g., MORNING RUN, READ, DRINK WATER"
                className="w-full border-4 border-neutral-900 bg-white px-4 py-3 text-neutral-900 placeholder-neutral-400 focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all font-mono font-bold uppercase"
              />
            </div>

            <div>
              <label className="label mb-2">Description (Optional)</label>
              <input
                type="text" value={newHabit.description} maxLength={MAX_TEXT_LENGTH}
                onChange={e => setNewHabit({ ...newHabit, description: e.target.value })}
                placeholder="Why is this important?"
                className="w-full border-4 border-neutral-900 bg-white px-4 py-3 text-neutral-900 placeholder-neutral-400 focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all font-mono"
              />
            </div>

            <div>
              <label className="label mb-3">Type *</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {typeButtons.map(({ type, label, sub }) => (
                  <button
                    key={type}
                    onClick={() => setNewHabit({ ...newHabit, type })}
                    className={`p-4 border-2 transition-all hover:-translate-x-0.5 hover:-translate-y-0.5 ${newHabit.type === type ? 'border-neutral-900 bg-neutral-900 text-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]' : 'border-neutral-900 bg-white hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]'}`}
                  >
                    <div className="font-bold mb-2 font-mono uppercase text-sm">{label}</div>
                    <div className="text-xs opacity-75 font-mono">{sub}</div>
                  </button>
                ))}
              </div>
            </div>

            {newHabit.type === 'quantity' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-2 border-neutral-900 p-4 bg-neutral-50">
                <div>
                  <label className="label mb-2">Unit *</label>
                  <input type="text" value={newHabit.unit} maxLength={MAX_UNIT_LENGTH}
                    onChange={e => setNewHabit({ ...newHabit, unit: e.target.value })}
                    placeholder="GLASSES, LBS, KM"
                    className="w-full border-2 border-neutral-900 bg-white px-3 py-2 text-neutral-900 focus:outline-none focus:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all font-mono font-bold uppercase"
                  />
                </div>
                <div>
                  <label className="label mb-2">Target</label>
                  <input type="number" step="0.1" min="0" max={MAX_TARGET} value={newHabit.target}
                    onChange={e => setNewHabit({ ...newHabit, target: e.target.value })}
                    placeholder="0"
                    className="w-full border-2 border-neutral-900 bg-white px-3 py-2 text-neutral-900 focus:outline-none focus:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all font-mono font-bold"
                  />
                </div>
              </div>
            )}

            {newHabit.type === 'time' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-2 border-neutral-900 p-4 bg-neutral-50">
                <div>
                  <label className="label mb-2">Target Hours</label>
                  <input type="number" min="0" max="23" value={newHabit.targetHours || ''}
                    onChange={e => setNewHabit({ ...newHabit, targetHours: e.target.value })}
                    placeholder="0"
                    className="w-full border-2 border-neutral-900 bg-white px-3 py-2 text-neutral-900 focus:outline-none focus:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all font-mono font-bold"
                  />
                </div>
                <div>
                  <label className="label mb-2">Target Minutes</label>
                  <input type="number" min="0" max="59" value={newHabit.targetMinutes || ''}
                    onChange={e => setNewHabit({ ...newHabit, targetMinutes: e.target.value })}
                    placeholder="0"
                    className="w-full border-2 border-neutral-900 bg-white px-3 py-2 text-neutral-900 focus:outline-none focus:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all font-mono font-bold"
                  />
                </div>
              </div>
            )}

            <button
              onClick={addHabit}
              disabled={!newHabit.name.trim()}
              className="w-full bg-neutral-900 text-white py-4 text-sm uppercase tracking-wider font-bold hover:bg-neutral-700 transition-all flex items-center justify-center gap-2 card-md hover-lift-xl font-mono disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] disabled:hover:translate-x-0 disabled:hover:translate-y-0"
            >
              <Plus className="w-5 h-5" /> Add Habit
            </button>
          </div>
        </div>

        {/* Habit list */}
        <div className="card-md bg-white p-4 sm:p-6">
          <div className="border-b-2 border-neutral-900 pb-4 mb-6">
            <h2 className="section-title">Your Habits ({habits.length})</h2>
          </div>

          {habits.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-neutral-900">
              <Target className="w-16 h-16 text-neutral-400 mx-auto mb-4 border-4 border-neutral-900 p-2" />
              <p className="text-neutral-500 font-mono uppercase tracking-wide">No habits yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {habits.map(habit => (
                <div key={habit.id} className="border-2 border-neutral-900 p-4 flex items-start justify-between hover-lift bg-white">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-neutral-900 mb-1 font-mono uppercase truncate">{habit.name}</h3>
                    {habit.description && <p className="text-sm text-neutral-600 mb-2 font-mono">{habit.description}</p>}
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="badge-dark">{habit.type === 'binary' ? '✓ CHECK' : habit.type === 'quantity' ? '# QUANTITY' : '⏱ TIME'}</span>
                      {habit.type !== 'binary' && habit.target && (
                        <span className="badge">Target: {habit.target} {habit.unit || 'min'}</span>
                      )}
                      {habit.type === 'time' && (habit.targetHours || habit.targetMinutes) && (
                        <span className="badge">Target: {habit.targetHours || 0}h {habit.targetMinutes || 0}m</span>
                      )}
                    </div>
                  </div>
                  <button onClick={() => setDeleteConfirm(habit.id)} className="ml-4 flex-shrink-0 border-2 border-neutral-900 bg-white text-neutral-900 hover:bg-red-600 hover:text-white hover:border-red-600 transition-all p-2">
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
          <div className="bg-white border-4 border-neutral-900 max-w-sm w-full shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] p-6">
            <h3 className="text-xl font-serif text-neutral-900 uppercase mb-2">Delete habit?</h3>
            <p className="text-sm font-mono text-neutral-600 mb-6">This will remove all completion history for this habit.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 border-2 border-neutral-900 py-3 text-sm uppercase font-bold font-mono hover-lift">Cancel</button>
              <button onClick={() => deleteHabit(deleteConfirm)} className="flex-1 bg-red-600 text-white py-3 text-sm uppercase font-bold font-mono border-2 border-neutral-900 hover:bg-red-700 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Habits;